import axios from 'axios';
import { useAuthStore } from '../store/authStore';

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

export const api = axios.create({ baseURL: API_BASE_URL });

// Attach the access token to every request unless explicitly skipped.
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token && !config.skipAuth) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Single-flight refresh: many concurrent 401s share one refresh round-trip.
let refreshPromise = null;

async function performRefresh() {
  const refreshToken = useAuthStore.getState().refreshToken;
  if (!refreshToken) throw new Error('No refresh token');
  // Use a bare axios call so this request isn't itself intercepted/looped.
  const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken });
  useAuthStore.getState().setSession(data);
  return data.accessToken;
}

// A dead impersonated session falls back to the stashed admin session instead
// of nuking everything — the super admin never gets logged out by proxy.
function dropSession() {
  const store = useAuthStore.getState();
  if (store.impersonation) {
    store.endImpersonation();
  } else {
    store.clear();
  }
}

function blobText(blob) {
  if (typeof blob.text === 'function') return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

/**
 * Downloads ask for a Blob, so an error body arrives as a Blob too. Replace it
 * with the JSON inside so callers can show the server's message (for example a
 * file blocked by the malware scan) instead of a bare status code.
 */
export async function readBlobErrorBody(response) {
  if (
    typeof Blob === 'undefined' ||
    !(response?.data instanceof Blob) ||
    !response.data.type?.includes('json')
  ) {
    return;
  }
  try {
    response.data = JSON.parse(await blobText(response.data));
  } catch {
    // Leave the Blob as it is; callers fall back to their own message.
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config } = error;
    await readBlobErrorBody(response);
    if (!response || response.status !== 401 || config?._retry || config?.skipAuth) {
      return Promise.reject(error);
    }
    if (!useAuthStore.getState().refreshToken) {
      dropSession();
      return Promise.reject(error);
    }
    try {
      if (!refreshPromise) {
        refreshPromise = performRefresh().finally(() => {
          refreshPromise = null;
        });
      }
      const newToken = await refreshPromise;
      config._retry = true;
      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${newToken}`;
      return api(config);
    } catch (refreshError) {
      dropSession();
      return Promise.reject(refreshError);
    }
  },
);

/** Extract a human-readable message from an axios error. */
export function getErrorMessage(error, fallback = 'Something went wrong') {
  const message = error?.response?.data?.message;
  if (Array.isArray(message)) return message[0];
  if (typeof message === 'string') return message;
  return error?.message || fallback;
}

/** True when the backend refused with a 422 plan-limit error (seats/workspaces). */
export function isLimitReached(error) {
  return error?.response?.data?.code === 'LIMIT_REACHED';
}
